import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { acceptCreatorTerms } from './creatorApi';

const sectionHeaderSx = {
  fontWeight: 700,
  mt: 3,
  mb: 1,
  color: 'text.primary',
};

const clauseBoxSx = {
  p: 2,
  mb: 2,
  bgcolor: 'grey.50',
  borderLeft: '3px solid',
  borderColor: 'primary.main',
};

const highlightBoxSx = (color: 'success' | 'warning') => ({
  p: 2,
  mb: 2,
  bgcolor: color === 'success' ? 'success.50' : 'warning.50',
  border: '1px solid',
  borderColor: color === 'success' ? 'success.light' : 'warning.light',
  borderRadius: 1,
});

const CreatorAgreement: React.FC = () => {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptCreatorTerms();
      setAccepted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept agreement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (accepted) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome to the Snack'd Creator Program!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3, lineHeight: 1.8 }}>
          Thank you for joining our creator program. We are excited to work with you!
        </Typography>
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', textAlign: 'left' }}>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            One last step — please complete your account setup so we can personalize your
            experience. Once you're done, you can find your <strong>Creator Dashboard</strong> under
            the "My Account" dropdown in the top navigation, where you'll see your promo code,
            track referrals, and manage payouts.
          </Typography>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If you have any questions, please contact us at{' '}
          <MuiLink href="mailto:marketing@snackdapp.com">marketing@snackdapp.com</MuiLink>
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={RouterLink}
          to="/onboarding/plan"
        >
          Complete Account Setup
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Creator Affiliate Agreement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please review and accept the following agreement to access your creator dashboard.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          maxHeight: 500,
          overflow: 'auto',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Snack'd Creator Affiliate Agreement
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Effective Date: Upon acceptance by Creator
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" paragraph>
          This Creator Affiliate Agreement ("Agreement") is a legal contract between you
          ("Creator," "you," or "your") and Snack'd ("Company," "we," "us," or "our"),
          the operator of the Snack'd meal planning application. By accepting this Agreement,
          you agree to be bound by the following terms and conditions.
        </Typography>

        {/* Section 1 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          1. Program Overview
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            The Snack'd Creator Affiliate Program allows approved creators to earn commissions
            by promoting Snack'd through unique promo codes. When new users sign up using your
            promo code, they receive an extended free trial period, and you earn a commission on
            their subsequent paid subscriptions.
          </Typography>
        </Paper>

        {/* Section 2 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          2. Commission Structure
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Commission Rate:</strong> You will earn a <strong>25% recurring commission</strong> on
                net revenue (gross subscription revenue minus platform transaction fees including Apple App Store,
                Google Play Store, and Stripe processing fees) generated by users who subscribe using your
                unique promo code.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Recurring Commissions:</strong> You will continue to earn commission for as long as
                the referred user maintains an active paid subscription, for the lifetime of their subscription.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Attribution:</strong> A referral is attributed to you when a new user signs up using your
                unique promo code. This attribution is permanent for that user.
              </li>
              <li>
                <strong>Commission Calculation:</strong> Commissions are calculated monthly based on the
                previous month's subscription payments from your referred users.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 3 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          3. Payout Terms
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Payout Method:</strong> All payouts are processed via PayPal. You are responsible
                for maintaining a valid PayPal account.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Payout Schedule:</strong> Payouts are processed on the 1st of each month for the
                prior month's earnings, and typically arrive within 5-7 business days.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Minimum Payout:</strong> There is no minimum payout threshold. You may request a
                payout for any positive balance.
              </li>
              <li>
                <strong>Tax Responsibility:</strong> You are solely responsible for reporting and paying
                any applicable taxes on your earnings. The Company may request tax documentation (e.g.,
                W-9 for US-based creators) as required by law.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 4 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          4. Promotional Guidelines
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            When promoting Snack'd, you agree to represent the product honestly and in good faith.
            We ask that you attempt to follow our Creator Program Guidelines, found in your
            dashboard assets, as best you can.
          </Typography>

          <Box sx={{ ...highlightBoxSx('success'), bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="success.dark">
              Do:
            </Typography>
            <Typography variant="body2" component="div" color="success.dark">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Share your genuine experience with Snack'd</li>
                <li>Clearly communicate what Snack'd does (AI meal planning for families)</li>
                <li>Disclose your affiliate relationship (e.g., "I earn a commission if you sign up")</li>
                <li>Follow FTC and applicable advertising disclosure guidelines</li>
                <li>Use your own creative style and voice</li>
              </ul>
            </Typography>
          </Box>

          <Box sx={{ ...highlightBoxSx('warning'), bgcolor: '#fef9c3', borderColor: '#fde047' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="warning.dark">
              Don't:
            </Typography>
            <Typography variant="body2" component="div" color="warning.dark">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Make false, misleading, or unsubstantiated claims about Snack'd</li>
                <li>Engage in spam, unsolicited messaging, or deceptive practices</li>
                <li>Imply endorsement by any third party without authorization</li>
                <li>Use your promo code for your own sign-ups or purchases</li>
                <li>Create fake accounts or engage in fraudulent referral activity</li>
              </ul>
            </Typography>
          </Box>
        </Paper>

        {/* Section 5 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          5. Prohibited Activities
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            The following activities are strictly prohibited and may result in immediate
            termination of your creator account:
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 4 }}>Using your promo code for personal sign-ups or purchases</li>
              <li style={{ marginBottom: 4 }}>Creating fake accounts or generating fraudulent referrals</li>
              <li style={{ marginBottom: 4 }}>Bidding on Snack'd branded keywords in paid advertising without prior written approval</li>
              <li style={{ marginBottom: 4 }}>Sending unsolicited bulk messages (spam) promoting your code</li>
              <li style={{ marginBottom: 4 }}>Misrepresenting Snack'd's features, pricing, or capabilities</li>
              <li>Any activity that violates applicable laws or regulations</li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 6 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          6. Creator Membership Benefits
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                As an active creator, you receive a <strong>complimentary Snack'd premium membership</strong> for
                the duration of your participation in the program.
              </li>
              <li style={{ marginBottom: 8 }}>
                This membership is granted at the Company's discretion and may be renewed based on
                your ongoing participation and contribution to the program.
              </li>
              <li>
                If your creator account is terminated, your complimentary membership will end at the
                close of the current membership period.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 7 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          7. Intellectual Property
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                The Company grants you a limited, non-exclusive, revocable license to use the Snack'd
                name, logo, and approved marketing materials solely for the purpose of promoting Snack'd
                as part of this program.
              </li>
              <li style={{ marginBottom: 8 }}>
                You may not alter, modify, or create derivative works of the Snack'd brand assets
                without prior written approval.
              </li>
              <li>
                All content you create remains your own property, but you grant the Company a
                non-exclusive license to feature or share your promotional content for marketing purposes.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 8 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          8. Confidentiality
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            You agree to keep confidential any non-public information shared with you through the
            Creator Program, including but not limited to: unreleased features, internal statistics,
            commission rates of other creators, and business strategies. This obligation survives
            termination of this Agreement.
          </Typography>
        </Paper>

        {/* Section 9 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          9. Limitation of Liability
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                The Company provides the Creator Program "as is" without warranties of any kind.
              </li>
              <li style={{ marginBottom: 8 }}>
                The Company shall not be liable for any indirect, incidental, special, or consequential
                damages arising from your participation in the program.
              </li>
              <li>
                The Company's total liability under this Agreement shall not exceed the total
                commissions paid to you in the twelve (12) months preceding the claim.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 10 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          10. Termination
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                Either party may terminate this Agreement at any time for any reason with written notice.
              </li>
              <li style={{ marginBottom: 8 }}>
                The Company may immediately terminate or suspend your account without notice for violation
                of any prohibited activities or terms of this Agreement.
              </li>
              <li style={{ marginBottom: 8 }}>
                Upon termination, any pending commissions with a positive balance will be paid out
                within 30 days to the payment method on file.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Termination for cause (violation of prohibited activities):</strong> All commission
                accrual stops immediately on the date of termination, including commissions from referrals
                made prior to termination.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Termination without cause:</strong> Commissions from existing referrals will continue
                to accrue for 180 days following the date of termination, after which all commission accrual
                will cease.
              </li>
              <li style={{ marginBottom: 8 }}>
                Your complimentary Snack'd membership will remain active until the end of your current
                membership period, after which access will end.
              </li>
              <li>
                Termination does not affect any obligations that by their nature should survive, including
                confidentiality, limitation of liability, and intellectual property provisions.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 11 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          11. Relationship of Parties
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            You are an independent contractor. Nothing in this Agreement creates an employment,
            partnership, joint venture, or agency relationship between you and the Company. You are
            not authorized to make any commitments or representations on behalf of the Company.
          </Typography>
        </Paper>

        {/* Section 12 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          12. Modifications to This Agreement
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                The Company reserves the right to modify this Agreement at any time.
              </li>
              <li style={{ marginBottom: 8 }}>
                You will be notified of material changes at least <strong>14 days</strong> in advance
                via email or through your Creator Dashboard.
              </li>
              <li>
                Continued participation in the program after modifications take effect constitutes
                your acceptance of the updated terms.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 13 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          13. Governing Law & Dispute Resolution
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                This Agreement shall be governed by the laws of the State of California, United States.
              </li>
              <li style={{ marginBottom: 8 }}>
                Any disputes arising from this Agreement shall first be attempted to be resolved through
                good-faith negotiation between the parties.
              </li>
              <li>
                If negotiation fails, disputes shall be resolved through binding arbitration in
                accordance with the rules of the American Arbitration Association.
              </li>
            </ul>
          </Typography>
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          By checking the box below and clicking "Accept & Continue," you acknowledge that you have
          read, understood, and agree to be bound by the terms of this Creator Affiliate Agreement.
        </Typography>
      </Paper>

      <FormControlLabel
        control={
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
        }
        label="I have read and agree to the Creator Affiliate Agreement"
        sx={{ mb: 3 }}
      />

      <Box>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={!agreed || submitting}
        >
          {submitting ? <CircularProgress size={24} /> : 'Accept & Continue'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreatorAgreement;
